/**
 * Equipment Modifier Service
 * Applies craftsmanship and modifications to weapons and armor
 */

import type { Weapon, Armor, Craftsmanship } from '../types/character';
import type { AmmunitionEffectResult } from '../types/ammunition';
import { gameDataService } from './GameDataService';
import { getPayload } from './LauncherPayloadService';
import { AmmunitionService } from './AmmunitionService';
import { resolveArmorStats } from './ArmorResolutionService';

export interface ModifiedWeapon extends Weapon {
  name: string; // Resolved display name for internal use
  displayName: string;
  baseDamage: string;
  finalDamage: string;
  basePenetration: number;
  finalPenetration: number;
  attackBonus: number;
  parryBonus: number;
  bsBonus: number;
  finalSpecial: string[];
  specialParams?: Record<string, number>;
  modifierSummary: string[];
  /** Loaded ammo effect result for combat pipeline use */
  ammoEffects?: AmmunitionEffectResult;
  /** Effective clip (may be overridden by ammo like Hot-Shot) */
  effectiveClip?: number;
}

export interface ModifiedArmor extends Armor {
  displayName: string;
  baseArmorPoints: number;
  finalArmorPoints: number;
  modifierSummary: string[];
}

export class EquipmentModifierService {
  /**
   * Apply all modifiers to a weapon and return calculated stats
   */
  static applyWeaponModifiers(weapon: Weapon): ModifiedWeapon {
    const craftsmanship = weapon.craftsmanship || 'Common';
    const modifications = weapon.modifications || [];

    // Resolve base name from sourceId
    const baseName = gameDataService.getItemDisplayName(weapon.sourceId);
    let displayName = baseName;
    let attackBonus = 0;
    let parryBonus = 0;
    let bsBonus = 0;
    let damageBonus = 0;
    let penetrationBonus = 0;
    const finalSpecial = [...weapon.special];
    const modifierSummary: string[] = [];
    const namePrefixes: string[] = [];
    const nameSuffixes: string[] = [];

    // Apply craftsmanship
    const craftEffect = this.applyCraftsmanship(weapon, craftsmanship, modifierSummary);
    attackBonus += craftEffect.attackBonus;
    damageBonus += craftEffect.damageBonus;

    if (craftEffect.addReliable && !finalSpecial.includes('Reliable')) {
      finalSpecial.push('Reliable');
    }
    if (craftEffect.addUnreliable && !finalSpecial.includes('Unreliable')) {
      finalSpecial.push('Unreliable');
    }
    if (craftEffect.removeUnreliable) {
      const index = finalSpecial.indexOf('Unreliable');
      if (index > -1) finalSpecial.splice(index, 1);
    }

    // Add craftsmanship to name if not Common
    if (craftsmanship !== 'Common') {
      namePrefixes.push(craftsmanship);
    }

    // Apply modifications
    for (const mod of modifications) {
      const modEffect = this.applyModification(weapon, mod, modifierSummary);

      // Collect name modifiers
      if (modEffect.namePrefix) {
        namePrefixes.push(modEffect.namePrefix);
      }
      if (modEffect.nameSuffix) {
        nameSuffixes.push(modEffect.nameSuffix);
      }

      attackBonus += modEffect.attackBonus;
      parryBonus += modEffect.parryBonus;
      bsBonus += modEffect.bsBonus;
      damageBonus += modEffect.damageBonus;
      penetrationBonus += modEffect.penetrationBonus;

      // Handle special qualities
      if (modEffect.removeSpecial) {
        modEffect.removeSpecial.forEach((special) => {
          const index = finalSpecial.indexOf(special);
          if (index > -1) finalSpecial.splice(index, 1);
        });
      }
      if (modEffect.addSpecial) {
        modEffect.addSpecial.forEach((special) => {
          if (!finalSpecial.includes(special)) {
            finalSpecial.push(special);
          }
        });
      }
    }

    // Apply ammunition effects (after craftsmanship and modifications)
    let ammoEffects: AmmunitionEffectResult | undefined;
    let effectiveClip: number | undefined;
    // A launcher has no damage of its own; the loaded payload supplies it.
    let payloadDamage: string | undefined;
    let payloadDamageType: string | undefined;

    if (weapon.loadedAmmoId) {
      const ammoResult = AmmunitionService.applyEffects(weapon.loadedAmmoId);
      if (ammoResult) {
        ammoEffects = ammoResult;
        damageBonus += ammoResult.damageBonus;

        // Handle penetration (add vs set)
        if (ammoResult.penetration) {
          if (ammoResult.penetration.type === 'set') {
            // Compute delta needed to reach the target value from base penetration
            penetrationBonus = ammoResult.penetration.value - weapon.penetration;
          } else {
            penetrationBonus += ammoResult.penetration.value;
          }
        }

        // Handle attack modifier
        attackBonus += ammoResult.attackModifier;

        // Handle quality removals
        ammoResult.qualitiesRemoved.forEach((q) => {
          const idx = finalSpecial.indexOf(q);
          if (idx > -1) finalSpecial.splice(idx, 1);
        });

        // Handle quality additions
        ammoResult.qualitiesAdded.forEach((q) => {
          if (!finalSpecial.includes(q)) {
            finalSpecial.push(q);
          }
        });

        // Handle clip override
        if (ammoResult.clipOverride !== null) {
          effectiveClip = ammoResult.clipOverride;
        }

        // Handle damage type override
        // (stored in ammoEffects for combat pipeline; display name reflects it)

        // Add ammo name to display
        nameSuffixes.push(ammoResult.ammoName);
        modifierSummary.push(`${ammoResult.ammoName}: ammo effects applied`);
      } else {
        // Not one of the nine unusual ammunition types. A launcher's loadedAmmoId
        // holds a grenade instead, and that grenade REPLACES the launcher's
        // statistics rather than modifying them — every launcher prints "Variable"
        // for damage and penetration precisely because the round decides.
        const payload = getPayload(weapon.loadedAmmoId);
        if (payload) {
          payloadDamage = payload.damage;
          payloadDamageType = payload.damageType;
          // Reach the payload's value from the launcher's own, which is not a number.
          const launcherPen = typeof weapon.penetration === 'number' ? weapon.penetration : 0;
          penetrationBonus = payload.penetration - launcherPen;
          payload.special.forEach((q) => {
            if (!finalSpecial.includes(q)) finalSpecial.push(q);
          });
          nameSuffixes.push(payload.name);
          modifierSummary.push(`${payload.name}: payload statistics applied`);
        } else {
          // loadedAmmoId references neither an ammunition definition nor a payload
          console.warn(
            `[EquipmentModifierService] Weapon "${weapon.sourceId}" has loadedAmmoId "${weapon.loadedAmmoId}" but no matching ammunition definition or launcher payload was found. Treating as standard ammo.`
          );
        }
      }
    }

    // Build display name: Craftsmanship Modification BaseName (Suffix)
    // Example: "Best Mono Sword" or "Bolt Pistol (Red-Dot Laser Sight)"
    if (namePrefixes.length > 0) {
      displayName = `${namePrefixes.join(' ')} ${displayName}`;
    }
    if (nameSuffixes.length > 0) {
      displayName = `${displayName} (${nameSuffixes.join(', ')})`;
    }

    // Calculate final damage. A loaded payload supplies the damage outright.
    const totalDamageBonus = damageBonus;
    const baseDamage = payloadDamage ?? weapon.damage;
    const finalDamage = this.calculateFinalDamage(baseDamage, totalDamageBonus);

    // Calculate final penetration. Launchers print "Variable" rather than a number, so
    // adding a bonus to it produced the string "Variable0" — coerce the base first.
    const basePen = typeof weapon.penetration === 'number' ? weapon.penetration : 0;
    const finalPenetration = basePen + penetrationBonus;

    return {
      ...weapon,
      name: baseName, // Resolved name for internal use
      displayName,
      baseDamage,
      finalDamage,
      ...(payloadDamageType
        ? { damageType: payloadDamageType as ModifiedWeapon['damageType'] }
        : {}),
      basePenetration: basePen,
      finalPenetration,
      attackBonus,
      parryBonus,
      bsBonus,
      finalSpecial,
      specialParams: weapon.specialParams,
      modifierSummary,
      ammoEffects,
      effectiveClip
    };
  }

  /**
   * Apply all modifiers to armor and return calculated stats
   */
  static applyArmorModifiers(armor: Armor): ModifiedArmor {
    const craftsmanship = armor.craftsmanship || 'Common';
    // Resolve base name from sourceId
    const baseName = gameDataService.getItemDisplayName(armor.sourceId);
    let displayName = baseName;
    let armorPointsBonus = 0;
    const modifierSummary: string[] = [];

    // Apply craftsmanship
    switch (craftsmanship) {
      case 'Poor':
        modifierSummary.push('Poor Craftsmanship: May degrade when damaged');
        break;
      case 'Good':
        armorPointsBonus = 1;
        modifierSummary.push('Good Craftsmanship: +1 AP');
        break;
      case 'Best':
        armorPointsBonus = 2;
        modifierSummary.push('Best Craftsmanship: +2 AP');
        break;
    }

    // Add craftsmanship to display name if not Common
    if (craftsmanship !== 'Common') {
      displayName = `${displayName} (${craftsmanship})`;
    }

    return {
      ...armor,
      displayName,
      baseArmorPoints: resolveArmorStats(armor).armorPoints,
      finalArmorPoints: resolveArmorStats(armor).armorPoints + armorPointsBonus,
      modifierSummary
    };
  }

  /**
   * Apply craftsmanship effects
   */
  private static applyCraftsmanship(
    weapon: Weapon,
    craftsmanship: Craftsmanship,
    summary: string[]
  ): {
    attackBonus: number;
    damageBonus: number;
    addReliable: boolean;
    addUnreliable: boolean;
    removeUnreliable: boolean;
  } {
    const isMelee = weapon.class === 'melee';
    let attackBonus = 0;
    let damageBonus = 0;
    let addReliable = false;
    let addUnreliable = false;
    let removeUnreliable = false;

    switch (craftsmanship) {
      case 'Poor':
        if (isMelee) {
          attackBonus = -10;
          summary.push('Poor Craftsmanship: -10 to attack and parry');
        } else {
          addUnreliable = true;
          summary.push('Poor Craftsmanship: Unreliable (jams on failed hit if already Unreliable)');
        }
        break;

      case 'Good':
        if (isMelee) {
          attackBonus = 5;
          summary.push('Good Craftsmanship: +5 to attack');
        } else {
          addReliable = true;
          removeUnreliable = true;
          summary.push('Good Craftsmanship: Reliable (cancels Unreliable)');
        }
        break;

      case 'Best':
        if (isMelee) {
          attackBonus = 10;
          damageBonus = 1;
          summary.push('Best Craftsmanship: +10 to attack, +1 damage');
        } else {
          summary.push('Best Craftsmanship: Never jams or overheats');
        }
        break;
    }

    return { attackBonus, damageBonus, addReliable, addUnreliable, removeUnreliable };
  }

  /**
   * Apply modification effects
   */
  private static applyModification(
    weapon: Weapon,
    modification: string,
    summary: string[]
  ): {
    namePrefix?: string;
    nameSuffix?: string;
    attackBonus: number;
    parryBonus: number;
    bsBonus: number;
    damageBonus: number;
    penetrationBonus: number;
    removeSpecial?: string[];
    addSpecial?: string[];
  } {
    const mod = modification.toLowerCase();

    // Mono modification (handles both "mono" and "mono-upgrade")
    if (mod === 'mono' || mod === 'mono-upgrade' || mod.includes('mono')) {
      summary.push('Mono: +2 Penetration, removes Primitive');
      return {
        namePrefix: 'Mono',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 2,
        removeSpecial: ['Primitive']
      };
    }

    // Red-Dot Laser Sight
    if (mod.includes('red-dot') || mod.includes('red dot')) {
      summary.push('Red-Dot Laser Sight: +10 BS on single shot');
      return {
        nameSuffix: 'Red-Dot Laser Sight',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 10, // Only applies on single shot
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Telescopic Sight
    if (mod.includes('telescopic')) {
      summary.push('Telescopic Sight: +10 BS at long range, +20 at extreme range');
      return {
        nameSuffix: 'Telescopic Sight',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 10, // Simplified, actual bonus depends on range
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Silencer
    if (mod.includes('silencer') || mod.includes('suppressor')) {
      summary.push('Silencer: -20 to Awareness to hear shots, half detection distance');
      return {
        nameSuffix: 'Silencer',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Compact — Ch05: halves weight, clip, range; -1 Damage; -20 to find concealed
    if (mod === 'compact' || mod === 'compact-upgrade') {
      summary.push('Compact: -1 Damage, halved clip & range, -20 to find concealed');
      return {
        namePrefix: 'Compact',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: -1,
        penetrationBonus: 0
      };
    }

    // Fire Selector — Ch05: up to 3 clips, switch at start of turn
    if (mod.includes('fire-selector') || mod.includes('fire selector')) {
      summary.push('Fire Selector: up to 3 clips, switch at start of turn');
      return {
        nameSuffix: 'Fire Selector',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Forearm Weapon Mounting — Ch05: keeps hands free, -30% range
    if (mod.includes('forearm') || mod.includes('forearm-weapon')) {
      summary.push('Forearm Mounting: hands free, -30% range');
      return {
        nameSuffix: 'Forearm Mount',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Melee Attachment — Ch05: counts as spear in close combat
    if (
      mod.includes('melee-attachment') ||
      mod.includes('melee attachment') ||
      mod.includes('bayonet')
    ) {
      summary.push('Melee Attachment: counts as spear in close combat');
      return {
        nameSuffix: 'Melee Attachment',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Motion Predictor — Ch05: +10 BS on semi-auto or full-auto only
    if (mod.includes('motion-predictor') || mod.includes('motion predictor')) {
      summary.push('Motion Predictor: +10 BS on semi-auto/full-auto only');
      return {
        nameSuffix: 'Motion Predictor',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 10,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Photo Sight — Ch05: no penalties due to darkness; counts as sight
    if (mod.includes('photo-sight') || mod.includes('photo sight')) {
      summary.push('Photo Sight: no penalties due to darkness');
      return {
        nameSuffix: 'Photo Sight',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Preysense Sight — Ch05: no darkness penalties, +20 vision Perception at night; counts as sight
    if (mod.includes('preysense')) {
      summary.push('Preysense Sight: no darkness penalties, +20 vision Perception at night');
      return {
        nameSuffix: 'Preysense Sight',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Omni-scope — Ch05: combines Red-Dot (+10 BS single), Telescopic, Preysense; counts as sight
    if (mod.includes('omni-scope') || mod.includes('omni scope') || mod === 'omniscope') {
      summary.push(
        'Omni-scope: +10 BS single shot, ignores long/extreme range penalties (Full Aim), no darkness penalties, +20 vision Perception at night'
      );
      return {
        nameSuffix: 'Omni-scope',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 10,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Overcharge Pack — Ch05: +1 Damage, clip halved; Las weapons only
    if (mod.includes('overcharge')) {
      summary.push('Overcharge Pack: +1 Damage, clip halved');
      return {
        nameSuffix: 'Overcharge',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 1,
        penetrationBonus: 0
      };
    }

    // Suspensors — Ch05: halves weight, Auto-stabilized (always braced), semi/full-auto as Half Action
    if (mod.includes('suspensor')) {
      summary.push('Suspensors: Auto-stabilized (always braced), semi/full-auto as Half Action');
      return {
        nameSuffix: 'Suspensors',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0,
        addSpecial: ['Auto-stabilized']
      };
    }

    // Vox Operated — Ch05: fire, switch modes, operate fire selector by voice command
    if (mod.includes('vox-operated') || mod.includes('vox operated')) {
      summary.push('Vox Operated: fire, switch modes, operate fire selector by voice');
      return {
        nameSuffix: 'Vox Operated',
        attackBonus: 0,
        parryBonus: 0,
        bsBonus: 0,
        damageBonus: 0,
        penetrationBonus: 0
      };
    }

    // Default: unknown modification
    summary.push(`${modification}: Custom modification`);
    return {
      nameSuffix: modification,
      attackBonus: 0,
      parryBonus: 0,
      bsBonus: 0,
      damageBonus: 0,
      penetrationBonus: 0
    };
  }

  /**
   * Calculate final damage string with bonuses
   */
  private static calculateFinalDamage(baseDamage: string, totalBonus: number): string {
    if (totalBonus === 0) return baseDamage;

    // Parse damage string (e.g., "1d10+2 E" or "1d10 R")
    const match = baseDamage.match(/^(\d+d\d+)([+-]\d+)?\s*([A-Z])?$/i);
    if (!match) return baseDamage; // Can't parse, return as-is

    const dice = match[1]; // e.g., "1d10"
    const existingBonus = match[2] ? parseInt(match[2]) : 0;
    const damageType = match[3] || '';

    const finalBonus = existingBonus + totalBonus;
    const bonusStr = finalBonus >= 0 ? `+${finalBonus}` : `${finalBonus}`;

    return `${dice}${bonusStr}${damageType ? ' ' + damageType : ''}`;
  }

  /**
   * Get a summary of all modifiers for display
   */
  static getModifierSummary(weapon: Weapon): string {
    const modified = this.applyWeaponModifiers(weapon);
    return modified.modifierSummary.join('; ');
  }

  /**
   * Get display name with all modifications
   */
  static getDisplayName(equipment: Weapon | Armor): string {
    if ('class' in equipment) {
      // It's a weapon
      const modified = this.applyWeaponModifiers(equipment as Weapon);
      return modified.displayName;
    } else {
      // It's armor
      const modified = this.applyArmorModifiers(equipment as Armor);
      return modified.displayName;
    }
  }

  /**
   * Check if weapon has specific modification
   */
  static hasModification(weapon: Weapon, modificationName: string): boolean {
    return (
      weapon.modifications?.some((mod) =>
        mod.toLowerCase().includes(modificationName.toLowerCase())
      ) || false
    );
  }

  /**
   * Get craftsmanship display string
   */
  static getCraftsmanshipDisplay(craftsmanship?: Craftsmanship): string {
    if (!craftsmanship || craftsmanship === 'Common') return '';
    return craftsmanship;
  }
}
